import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import styles from './HeadacheCheckResults.module.css';
import headacheData from '../../data/headache.json';
import { addDoc, collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getUserId } from '../../utils/userId';

interface LocationState {
  answers: {
    questionId: string;
    optionId: string;
    value: boolean | number;
  }[];
}

interface RiskFactor {
  keyword: string;
  description: string;
}

interface FollowUpQuestion {
  id: string;
  text_key: string;
  riskFactor?: RiskFactor;
  options?: {
    optionTextKey: string;
    value: boolean | number;
  }[];
  followUpIfYes?: FollowUpQuestion[];
}

interface Question {
  id: string;
  type: string;
  questionTextKey: string;
  riskFactor?: RiskFactor;
  options: {
    optionTextKey: string;
    value: boolean | number;
  }[];
  followUpIfYes?: FollowUpQuestion[];
}

interface FormattedRiskFactor {
  keyword: string;
  description: string;
}

interface HeadacheResponseData {
  userId: string;
  timestamp: any;
  answers: any[];
  riskFactors: FormattedRiskFactor[];
  totalYesCount: number;
  shareDocId?: string;
}

const findQuestion = (id: string, questions: (Question | FollowUpQuestion)[]): Question | FollowUpQuestion | undefined => {
  for (const q of questions) {
      if (q.id === id) return q;
      if (q.followUpIfYes) {
          const result = findQuestion(id, q.followUpIfYes);
          if (result) return result;
      }
  }
  return undefined;
};


const HeadacheCheckResults: React.FC = () => {
  const { t, ready } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const answers = (location.state as LocationState)?.answers || [];
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';

  const [savingToFirebase, setSavingToFirebase] = useState(false);
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useLayoutEffect(() => {
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
  
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
  
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
  
      const timer = setTimeout(() => {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }, 50);
  
      return () => clearTimeout(timer);
    }, []);


  const yesCount = answers.filter(answer => answer.value === true).length;
  
  const { riskFactors, qaRiskFactors, total } = useMemo(() => {
    const riskFactors: FormattedRiskFactor[] = [];
    const qaRiskFactors: FormattedRiskFactor[] = [];

    // 1. Q&A 및 Risk Factor 생성 로직
    answers.forEach(answer => {
      if (answer.value === true) {
        let question = findQuestion(answer.questionId, headacheData.questions);

        if (question) {
            const questionKey = 'questionTextKey' in question 
                                ? question.questionTextKey 
                                : question.text_key;

            const questionText = t(questionKey); 
            const answerText = t(answer.optionId); 

            // 1. Risk Factor 목록 (기존 HeadacheCheckResults 페이지용)
            if (question.riskFactor) {
                const translatedRiskFactor = {
                    keyword: t(question.riskFactor.keyword),
                    description: t(question.riskFactor.description),
                };
                if (!riskFactors.some(rf => rf.keyword === translatedRiskFactor.keyword)) {
                    riskFactors.push(translatedRiskFactor);
                }
            }

            // 2. Q&A Risk Factor 목록 (HeadacheReport 페이지용)
            qaRiskFactors.push({
                keyword: questionText, 
                description: answerText, 
            });
        }
      }
    });
    
    // 2. Total Max Risk 계산 로직 (useMemo 내부에 정의)
    const calculateTotalMaxRisk = (questions: any[]): number => {
      let count = 0;
      questions.forEach((q) => {
        const hasRisk = q.riskFactor || q.options?.some((opt: any) => opt.riskFactor);
        if (hasRisk) {
          count++;
        }
        if (q.followUpIfYes) {
          count += calculateTotalMaxRisk(q.followUpIfYes);
        }
      });
      return count;
    };

    const total = calculateTotalMaxRisk(headacheData.questions);
    
    return { riskFactors, qaRiskFactors, total };
  }, [answers, t]); 

  
  const percentage = total === 0 ? 0 : (yesCount / total) * 100;

  let scoreClass = styles.scoreGreen; 
  if (percentage >= 60) { 
    scoreClass = styles.scoreRed;
  } else if (percentage >= 30) { 
    scoreClass = styles.scoreOrange;
  }

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      contentElement.classList.remove(styles.scrollHidden);
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        contentElement.classList.add(styles.scrollHidden);
      }, 400);
    };

    contentElement.addEventListener("scroll", handleScroll);
    contentElement.classList.add(styles.scrollHidden);

    return () => {
      contentElement.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [contentRef]);

  useEffect(() => {
    if (answers.length === 0) {
       navigate(`${prefix}/headache_check`);
       return;
    }

    const saveToFirebaseAndGenerateQr = async () => {
      if (!ready) {
        console.warn('i18n not ready, deferring save...');
        return;
      }

      setSavingToFirebase(true);
      setQrError(null);
      try {
        const userId = getUserId();
        if (!userId) {
          console.error('No user ID found');
          setQrError('사용자 ID가 없습니다.');
          return;
        }
        
        console.log('✅ Firestore 저장 시작...');
        
        const formattedAnswers = answers.map((answer: any) => {
          const question = findQuestion(answer.questionId, headacheData.questions);

          const questionKey = question 
            ? ('questionTextKey' in question 
                ? question.questionTextKey 
                : question.text_key) 
            : null;
            
          return {
            questionId: answer.questionId,
            questionText: questionKey ? t(questionKey) : '',
            answer: answer.value,
            answerText: t(answer.optionId), 
          };
        });

        const sharePayload = {
          kind: 'headache', 
          createdAt: serverTimestamp(),
          score: yesCount, 
          riskFactors: riskFactors,
          qaRiskFactors: qaRiskFactors, 
        };

        const shareRef = await addDoc(collection(db, 'sharedHeadacheResults'), sharePayload); 
        console.log('✅ Firestore addDoc 성공:', shareRef.id);

        const responseData: HeadacheResponseData = {
          userId,
          timestamp: serverTimestamp(),
          answers: formattedAnswers,
          riskFactors: riskFactors,
          totalYesCount: yesCount,
          shareDocId: shareRef.id,
        };
        
        // 문서 ID를 {userId}_{timestamp} 형태로 만듦 (UUID 사용)
        const docId = `${userId}_${Date.now()}`;
        const responseRef = doc(db, 'userHeadacheResponse', docId); 
        await setDoc(responseRef, responseData, {merge: true});

        setShareDocId(shareRef.id);

      } catch (err: any) {
        console.error("❌ Firestore addDoc error:", err.code, err.message);
        console.error(err);
        setQrError(err?.message || 'QR용 문서 생성에 실패했습니다.');
      } finally {
        setSavingToFirebase(false);
      }
    };

    if (location.state && location.state.answers && !shareDocId) {
        saveToFirebaseAndGenerateQr();
    }
  }, [answers, ready, t, riskFactors, qaRiskFactors]);

  useEffect(() => {
    console.log('🧩 shareDocId 업데이트됨:', shareDocId);
  }, [shareDocId]);

  const handleBack = () => {
    navigate(`${prefix}/headache_check`, { 
      state: { 
        answers: answers
      }
    });
  };

  const handleConfirm = () => {
    localStorage.removeItem('headacheAnswers');
    navigate(`${prefix}/headache_check`);
  };

  const publicOrigin = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin || "http://localhost:3000";
  const shareUrl = shareDocId ? `${publicOrigin}${prefix}/headache/report/${shareDocId}` : '';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('check_before_2')}</span>
      </div>
      
      <div className={styles.content}>
        <div className={styles.contentScroll} ref={contentRef}>
          <div className={styles.imageContainer}>
            <span className={styles.resultTitle}>{t("test_result_title")}</span>
          </div>
          <div className={styles.historyScore}>
            <div className={`${styles.scoreCircle} ${scoreClass}`}>
              {yesCount}
            </div>
            <span className={styles.scoreTotalText}>
              / {total}
            </span>
          </div>
          <div className={styles.resultDescription}>
            {t("test_result_description")}
          </div>
          {riskFactors.map((riskFactor, index) => (
            <div key={index} className={styles.riskFactorContainer}>
              <div className={styles.riskFactorKeyword}>
                {t(riskFactor?.keyword || "")}
              </div>
              <div className={styles.riskFactorDescription}>
                {t(riskFactor?.description || "")}
              </div>
            </div>
          ))}
        <div className={styles.qrCodeContainer}>
          {shareUrl ? (
            <>
              {console.log("QR 렌더링 시점:", shareDocId, shareUrl)}
              <div className={styles.qrWrapper}>
                <QRCodeCanvas
                  key={shareUrl}
                  value={shareUrl}
                  size={180}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                  includeMargin
                />
              </div>
              <div className={styles.qrCodeDescription}>
                {t("scan_qr_code")}
              </div>
            </>
          ) : (
             <>
            <div
              style={{
                height: 180,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.7,
                flexDirection: 'column'
              }}
            >
              {savingToFirebase 
                  ? t("generating_qr") || "QR 생성 중..."
                  : "..."}
            </div>
            {qrError && (
              <>
                <div style={{ fontSize: 12, opacity: 0.8, color: 'red' }}>({qrError})</div>
                <button
                  className={styles.copyLinkButton}
                  onClick={() => window.location.reload()}
                  style={{ marginTop: 4, background: '#888' }}
                >
                  {t("retry") || "다시 시도"}
                </button>
              </>
            )}
          </>
          )}
        </div>
        </div>
      </div>
      
      <button
        className={styles.confirmButton}
        onClick={handleConfirm}
        disabled={savingToFirebase} 
      >
        {savingToFirebase ? t("saving") : t("confirm_button_text")}
      </button>
    </div> 
  );
};

export default HeadacheCheckResults;